import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { marked } from "marked";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => nav("/blog"));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useSEO(
    post
      ? {
          title: `${post.title} | Sarthi Associates Blog`,
          description: post.excerpt,
          path: `/blog/${post.slug}`,
          image: post.cover_image,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.cover_image,
            "datePublished": post.created_at,
            "dateModified": post.updated_at,
            "author": { "@type": "Organization", "name": post.author || "Sarthi Associates" },
            "publisher": {
              "@type": "Organization",
              "name": "Sarthi Associates",
              "logo": { "@type": "ImageObject", "url": "https://sarthiassociates.com/sarthi-logo.png" },
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://sarthiassociates.com/blog/${post.slug}` },
            "keywords": (post.tags || []).join(", "),
          },
        }
      : { title: "Loading… | Sarthi Associates Blog", description: "", path: `/blog/${slug}` }
  );

  if (!post) return <div className="p-10 text-center text-sarthi-muted">Loading…</div>;
  const html = marked.parse(post.body || "", { breaks: true });

  return (
    <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Link to="/blog" data-testid="blog-back-link" className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-sarthi-muted hover:text-sarthi-primary">
        <ArrowLeft className="h-4 w-4" /> All articles
      </Link>

      <div className="text-xs font-bold uppercase tracking-wider text-sarthi-primary">{post.category}</div>
      <h1 className="mt-2 font-display text-4xl font-black leading-tight text-sarthi-text sm:text-5xl">
        {post.title}
      </h1>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold text-sarthi-muted">
        <span className="inline-flex items-center gap-1"><User className="h-4 w-4" /> {post.author}</span>
        <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> {(post.created_at || "").slice(0, 10)}</span>
        {(post.tags || []).length > 0 && (
          <span className="inline-flex items-center gap-1">
            <Tag className="h-4 w-4" />
            {post.tags.join(", ")}
          </span>
        )}
      </div>

      {post.cover_image && (
        <img
          src={post.cover_image}
          alt={post.title}
          className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover"
        />
      )}

      <div
        data-testid="blog-post-body"
        className="prose-sarthi mt-8"
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <div className="mt-12 rounded-2xl bg-sarthi-accent p-6 text-center">
        <div className="font-display text-2xl font-extrabold text-sarthi-text">Ready to work with Sarthi?</div>
        <p className="mt-2 text-base font-medium text-sarthi-muted">
          Whether you're hiring frontline crews or looking for your next site — we're one tap away.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link to="/jobs" className="sarthi-btn-primary px-5 py-3 text-white">Browse Jobs</Link>
          <Link to="/partners" className="sarthi-btn-outline px-5 py-3">Partner with us</Link>
        </div>
      </div>
    </article>
  );
}
